import { createContext, useCallback, useContext, useMemo } from "react"

import {
	ApolloCache,
	ApolloClient,
	ApolloProvider,
	DefaultContext,
	InMemoryCache,
	MutationHookOptions,
	MutationTuple,
	NoInfer,
	OperationVariables,
	QueryHookOptions,
	QueryResult,
	createHttpLink,
	from,
	useMutation,
	useQuery,
} from "@apollo/client"
import { onError } from "@apollo/client/link/error"

import { createApolloFetch } from "./customFetch/createApolloFetch"
import { queryTimingLink } from "./links/queryTimingLink"

import { getAppAlertMessage } from "@/main/domain/graphql"
import { getAppAlertMessagePublic } from "@/public/domain/graphql"

import { useLoading } from "../loading/useLoading"

import type { TypedDocumentNode } from "@graphql-typed-document-node/core"
import type { DocumentNode } from "graphql"

const noopStopLoading = () => undefined
const redactedResponseOperationNames = new Set(["GetDataSourceLoginPassword"])

const redactResponseIfNeeded = (operationName: string, response: unknown) => {
	if (!redactedResponseOperationNames.has(operationName)) return response
	return {
		...((response as Record<string, unknown>) ?? {}),
		data: "[REDACTED]",
	}
}

// createApolloFetchに統合していいかも
const errorLink = (
	isPublic: boolean,
	customErrorHandler?: (message: string) => void
) => {
	const getAlertMessage = isPublic
		? getAppAlertMessagePublic
		: getAppAlertMessage

	return onError((error) => {
		const { networkError, graphQLErrors, operation, response } = error
		const disableErrorDialog = operation.getContext().disableErrorDialog

		if (networkError || graphQLErrors) {
			console.log(
				"res",
				redactResponseIfNeeded(operation.operationName, response)
			)
			console.log("networkError", networkError)
			console.log("graphQLErrors", graphQLErrors)
			console.log(networkError?.message)
			console.log(networkError?.cause)
			console.log(networkError?.stack)
			console.log(networkError?.name)
		}

		if (!disableErrorDialog) {
			if (graphQLErrors) {
				const appAlertMessage = getAlertMessage(graphQLErrors[0])
				if (appAlertMessage) {
					if (customErrorHandler) {
						customErrorHandler(appAlertMessage)
					} else {
						alert(appAlertMessage)
					}
					return
				}
			}

			if (!isPublic) {
				// 再予約画面はエラー発生時にアラートを表示しないようにする (必要に応じて appliction code 側でアラートを表示するようにしている)
				// TODO: ⇧再考の余地あり
				const msg =
					"エラーが発生しました。しばらく経ってから再度お試しください。"
				if (customErrorHandler) {
					customErrorHandler(msg)
				} else {
					alert(msg)
				}
			}
		}
	})
}

const useApolloClient = (
	url: string,
	path: string,
	isPublic: boolean,
	currentSalonID?: number,
	currentUserID?: string,
	customErrorHandler?: (message: string) => void
) => {
	const { startLoading } = useLoading()
	const onRequestStart = useCallback(
		(skipLoading: boolean) => {
			if (skipLoading) {
				return noopStopLoading
			}
			return startLoading()
		},
		[startLoading]
	)

	// これがreinitializeされると、実行中のrequestがキャンセルされてしまうので(多分)、
	// むやみにこのコンポーネントが最レンダリングされないように注意。
	// currentSalonIDをstate管理すると、setCurrentSalonIDの時、変にreinitializeされるのでそのような構成にはしてない。
	const apolloClient = useMemo(() => {
		const httpLink = createHttpLink({
			uri: url + path,
			fetch: createApolloFetch({
				currentSalonID,
				currentUserID,
				onRequestStart,
			}),
		})

		return new ApolloClient({
			link: from([
				errorLink(isPublic, customErrorHandler),
				queryTimingLink,
				httpLink,
			]),
			cache: new InMemoryCache(), // single source of truth
		})
	}, [
		startLoading,
		currentSalonID,
		currentUserID,
		path,
		url,
		isPublic,
		onRequestStart,
		customErrorHandler,
	])

	return apolloClient
}

const Apollo = ({
	currentSalonID,
	currentUserID,
	children,
}: {
	currentSalonID: number
	currentUserID: string
	children: React.ReactNode
}) => {
	const url = process.env.REACT_APP_STORE_MANAGEMENT_API_URL
	if (!url) {
		throw new Error("REACT_APP_STORE_MANAGEMENT_API_URL is not set")
	}
	const apolloClient = useApolloClient(
		url,
		"",
		false,
		currentSalonID,
		currentUserID
	)
	return <ApolloProvider client={apolloClient}>{children}</ApolloProvider>
}

export const PublicIdpApollo = ({
	children,
}: {
	children: React.ReactNode
}) => {
	const url = process.env.REACT_APP_IDP_PUBLIC_GRAPHQL_URL
	if (!url) {
		throw new Error("REACT_APP_IDP_PUBLIC_GRAPHQL_URL is not set")
	}
	const apolloClient = useApolloClient(url, "/idp/public_graphql", true)
	return <ApolloProvider client={apolloClient}>{children}</ApolloProvider>
}

export const PublicApollo = ({ children }: { children: React.ReactNode }) => {
	const url = process.env.REACT_APP_PUBLIC_RERESERVATION_SUPERGRAPH_URL
	if (!url) {
		throw new Error("REACT_APP_PUBLIC_RERESERVATION_SUPERGRAPH_URL is not set")
	}
	const apolloClient = useApolloClient(url, "/", true)
	return <ApolloProvider client={apolloClient}>{children}</ApolloProvider>
}

// souhl_admin supergraph を Apollo (store-management-supergraph) と同時に使う場合のために別 context を持つ。
// - useMutation や useQuery を使うと、デフォルトでは最も近い ApolloProvider の client を使うようになっている。
// - SouhlAdminApollo が外側、Apollo が内側にある場合、useSouhlAdminQuery で souhl_admin client にアクセスする。
const SouhlAdminClientContext = createContext<ApolloClient<any> | null>(null)

export const useSouhlAdminClient = (): ApolloClient<any> => {
	const apolloClient = useContext(SouhlAdminClientContext)
	if (!apolloClient) {
		throw new Error("SouhlAdminClientContext is not set")
	}
	return apolloClient
}

export const useSouhlAdminQuery = <
	TData = any,
	TVariables extends OperationVariables = OperationVariables,
>(
	query: DocumentNode | TypedDocumentNode<TData, TVariables>,
	options?: QueryHookOptions<NoInfer<TData>, NoInfer<TVariables>>
): QueryResult<TData, TVariables> => {
	const apolloClient = useSouhlAdminClient()
	return useQuery(query, {
		...options,
		client: apolloClient,
	})
}

export const useSouhlAdminMutation = <
	TData = any,
	TVariables = OperationVariables,
	TContext = DefaultContext,
	TCache extends ApolloCache<any> = ApolloCache<any>,
>(
	mutation: DocumentNode | TypedDocumentNode<TData, TVariables>,
	options?: MutationHookOptions<
		NoInfer<TData>,
		NoInfer<TVariables>,
		TContext,
		TCache
	>
): MutationTuple<TData, TVariables, TContext, TCache> => {
	const apolloClient = useSouhlAdminClient()
	return useMutation(mutation, {
		...options,
		client: apolloClient,
	})
}

// --- Store Management Client Context ---
// SouhlAdminProvider 内から store-management supergraph にアクセスするための Context。
// ApolloProvider でラップしないため、デフォルトの useQuery/useMutation には影響しない。

const StoreManagementClientContext = createContext<ApolloClient<any> | null>(
	null
)

export const useStoreManagementQuery = <
	TData = any,
	TVariables extends OperationVariables = OperationVariables,
>(
	query: DocumentNode | TypedDocumentNode<TData, TVariables>,
	options?: QueryHookOptions<NoInfer<TData>, NoInfer<TVariables>>
): QueryResult<TData, TVariables> => {
	const apolloClient = useContext(StoreManagementClientContext)
	if (!apolloClient) {
		throw new Error("StoreManagementClientContext is not set")
	}
	return useQuery(query, {
		...options,
		client: apolloClient,
	})
}

export const useStoreManagementMutation = <
	TData = any,
	TVariables = OperationVariables,
	TContext = DefaultContext,
	TCache extends ApolloCache<any> = ApolloCache<any>,
>(
	mutation: DocumentNode | TypedDocumentNode<TData, TVariables>,
	options?: MutationHookOptions<
		NoInfer<TData>,
		NoInfer<TVariables>,
		TContext,
		TCache
	>
): MutationTuple<TData, TVariables, TContext, TCache> => {
	const apolloClient = useContext(StoreManagementClientContext)
	if (!apolloClient) {
		throw new Error("StoreManagementClientContext is not set")
	}
	return useMutation(mutation, {
		...options,
		client: apolloClient,
	})
}

export const StoreManagementClientProvider = ({
	currentSalonID,
	currentUserID,
	children,
}: {
	currentSalonID: number
	currentUserID: string
	children: React.ReactNode
}) => {
	const url = process.env.REACT_APP_STORE_MANAGEMENT_API_URL
	if (!url) {
		throw new Error("REACT_APP_STORE_MANAGEMENT_API_URL is not set")
	}
	const apolloClient = useApolloClient(
		url,
		"",
		false,
		currentSalonID,
		currentUserID
	)
	return (
		<StoreManagementClientContext.Provider value={apolloClient}>
			{children}
		</StoreManagementClientContext.Provider>
	)
}

// --- Company Admin Client Context ---
// company_admin supergraph を Apollo (store-management-supergraph) と同時に使う場合のために別 context を持つ。
const CompanyAdminClientContext = createContext<ApolloClient<any> | null>(null)

export const useCompanyAdminClient = (): ApolloClient<any> => {
	const apolloClient = useContext(CompanyAdminClientContext)
	if (!apolloClient) {
		throw new Error("CompanyAdminClientContext is not set")
	}
	return apolloClient
}

export const useCompanyAdminQuery = <
	TData = any,
	TVariables extends OperationVariables = OperationVariables,
>(
	query: DocumentNode | TypedDocumentNode<TData, TVariables>,
	options?: QueryHookOptions<NoInfer<TData>, NoInfer<TVariables>>
): QueryResult<TData, TVariables> => {
	const apolloClient = useCompanyAdminClient()
	return useQuery(query, {
		...options,
		client: apolloClient,
	})
}

export const useCompanyAdminMutation = <
	TData = any,
	TVariables = OperationVariables,
	TContext = DefaultContext,
	TCache extends ApolloCache<any> = ApolloCache<any>,
>(
	mutation: DocumentNode | TypedDocumentNode<TData, TVariables>,
	options?: MutationHookOptions<
		NoInfer<TData>,
		NoInfer<TVariables>,
		TContext,
		TCache
	>
): MutationTuple<TData, TVariables, TContext, TCache> => {
	const apolloClient = useContext(CompanyAdminClientContext)
	if (!apolloClient) {
		throw new Error("CompanyAdminClientContext is not set")
	}
	return useMutation(mutation, {
		...options,
		client: apolloClient,
	})
}

export const CompanyAdminApollo = ({
	currentUserID,
	currentSalonID,
	children,
}: {
	currentUserID: string
	currentSalonID: number
	children: React.ReactNode
}) => {
	const url = process.env.REACT_APP_COMPANY_ADMIN_SUPERGRAPH_URL
	if (!url) {
		throw new Error("REACT_APP_COMPANY_ADMIN_SUPERGRAPH_URL is not set")
	}
	const apolloClient = useApolloClient(
		url,
		"",
		false,
		currentSalonID,
		currentUserID
	)
	return (
		<CompanyAdminClientContext.Provider value={apolloClient}>
			{children}
		</CompanyAdminClientContext.Provider>
	)
}

export const SouhlAdminApollo = ({
	currentUserID,
	customErrorHandler,
	children,
}: {
	currentUserID: string
	customErrorHandler?: (message: string) => void
	children: React.ReactNode
}) => {
	const url = process.env.REACT_APP_SOUHL_ADMIN_SUPERGRAPH_URL
	if (!url) {
		throw new Error("REACT_APP_SOUHL_ADMIN_SUPERGRAPH_URL is not set")
	}
	const apolloClient = useApolloClient(
		url,
		"",
		false,
		undefined,
		currentUserID,
		customErrorHandler
	)
	return (
		<SouhlAdminClientContext.Provider value={apolloClient}>
			<ApolloProvider client={apolloClient}>{children}</ApolloProvider>
		</SouhlAdminClientContext.Provider>
	)
}

export default Apollo
